import {
  THEME_MODE_STORAGE_KEY,
  type ThemeMode,
  getSystemPrefersDark,
  readStoredMode,
} from '@/viewmodel/shared/theme/state/themeStorage';
import type { ThemeContextValue } from '@/viewmodel/shared/theme/themeContext';
import { useCallback, useEffect, useMemo, useState } from 'react';

export function useThemeMode(): ThemeContextValue {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  useEffect(() => {
    const root = globalThis.document.documentElement;

    const applyTheme = (isDark: boolean) => {
      const theme = isDark ? 'dark' : 'light';
      root.classList.remove('light', 'dark');
      root.classList.add(theme);
      root.dataset['theme'] = theme;
    };

    if (mode === 'system') {
      applyTheme(getSystemPrefersDark());

      if (typeof globalThis.window.matchMedia !== 'function') {
        return;
      }

      const mediaQuery = globalThis.window.matchMedia('(prefers-color-scheme: dark)');
      if (!mediaQuery) {
        return;
      }

      const handleChange = (e: MediaQueryListEvent) => {
        setSystemPrefersDark(e.matches);
        applyTheme(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    applyTheme(mode === 'dark');
  }, [mode]);

  const isDark = mode === 'dark' || (mode === 'system' && systemPrefersDark);

  const setMode = useCallback((newMode: ThemeMode) => {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, newMode);
    setModeState(newMode);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, isDark, setMode }),
    [mode, isDark, setMode],
  );

  return value;
}
